import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Select, Button, Form, Alert, Card, Row, Col } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'

import API, { API_VERSION, REQUEST_METHOD } from '../../../api'
// import { downloadFile } from '../../../common/utils/downloadFile'
import STATUS from '../../../common/constants/reducer_statuses'
import {
  fetchInspections,
  fetchForm,
  createInspectionSubmission,
  resetCreateInspectionSubmission,
  resetInspections,
} from '../../../store/reducers/inspection/inspectionSlice'

import {
  selectInspectionsData,
  selectInspectionsError,
  selectIsInspectionsLoading,
  selectCreateInspectionSubmissionStatus,
  selectCreateInspectionSubmissionError,
  selectInspectionSubmissionsData,
  selectInspectionSubmissionsError,
  selectIsFormLoading,
  // selectFormVersion,
} from '../../../store/reducers/inspection/inspectionSelectors'

import Loader from '../../../common/layout/components/Loader'
import InspectionSubmissionsView from './InspectionSubmissionsView'
import InspectionBackofficeStatus from './inspectionBackofficeStatus'
import CompleteDataCollectionButton from './CompleteDataCollectionButton'
import { isStreamlinedInspectionOrCompletionReportOrder } from '@helpers/pdr_orders'
import { ORDER_STATUS } from '../../../common/constants/orders'

const INVESTOR_FANNIE = 'fannie'
const INVESTOR_FREDDIE = 'freddie'
const INVESTOR_BOTH = 'both'

const INVESTOR_OPTIONS = [
  { value: INVESTOR_FANNIE, label: 'Fannie' },
  { value: INVESTOR_FREDDIE, label: 'Freddie' },
  { value: INVESTOR_BOTH, label: 'Fannie & Freddie' },
]
const UPD_MAPPING_VERSION_1_1 = 'updv1.1'
const INSPECTION_SUBMISSION_STATUS_PENDING = 'pending'

const InspectionSubmissionsTab = ({ order: { id: orderId }, orderData, is_amc_lender, refreshOrder }) => {
  const dispatch = useDispatch()

  const [inspectionId, setInspectionId] = useState('')
  const [formId, setFormId] = useState('')
  const [investor, setInvestor] = useState(INVESTOR_BOTH)

  const inspectionsData = useSelector(selectInspectionsData)
  const inspectionsError = useSelector(selectInspectionsError)
  const isInspectionsLoading = useSelector(selectIsInspectionsLoading)
  // const formVersion = useSelector(selectFormVersion)
  const isFormLoading = useSelector(selectIsFormLoading)

  const createInspectionSubmissionStatus = useSelector(selectCreateInspectionSubmissionStatus)
  const createInspectionSubmissionError = useSelector(selectCreateInspectionSubmissionError)

  const inspectionSubmissionsData = useSelector(selectInspectionSubmissionsData)
  const inspectionSubmissionsError = useSelector(selectInspectionSubmissionsError)

  const [inspectionDetailWescanFiles, setInspectionDetailWescanFiles] = useState([])

  useEffect(() => {
    dispatch(resetCreateInspectionSubmission())
    dispatch(resetInspections())
  }, [dispatch])

  useEffect(() => {
    const fetchInspectionsData = async () => {
      await dispatch(fetchInspections(orderId))
    }

    if (orderId) {
      fetchInspectionsData()
    }
  }, [orderId, dispatch])

  useEffect(() => {
    if (inspectionsData.length > 0) {
      setInspectionId(inspectionsData[0]._id)
      setFormId(inspectionsData[0].form_id)
    }
  }, [inspectionsData, dispatch])

  useEffect(() => {
    const fetchFormData = async () => {
      await dispatch(fetchForm(formId))
    }

    if (formId) {
      fetchFormData()
    }
  }, [formId, dispatch])

  useEffect(() => {
    const fetchInspectionDetailWescanFiles = async () => {
      const details = await API(
        `inspections/v1/${inspectionId}/details?metadata_source=wescan&presign_s3_urls=true`,
        API_VERSION.SERVICE,
      ).request(REQUEST_METHOD.GET)

      if (details.results.length > 0) {
        setInspectionDetailWescanFiles(details.results)
      }
    }

    if (inspectionId) {
      fetchInspectionDetailWescanFiles()
    }
  }, [inspectionId])

  const getSelectedInvestors = () => {
    return investor === INVESTOR_BOTH ? [INVESTOR_FANNIE, INVESTOR_FREDDIE] : [investor]
  }

  const onSubmit = () => {
    getSelectedInvestors().forEach((inv) => {
      dispatch(
        createInspectionSubmission({
          inspectionId,
          submission: {
            inspection_id: inspectionId,
            type: inv,
            mapping_version: UPD_MAPPING_VERSION_1_1,
            status: INSPECTION_SUBMISSION_STATUS_PENDING,
          },
        }),
      )
    })
  }

  const shouldDisableSubmit = (inspectionSubmissionsData || []).find(
    (submission) =>
      getSelectedInvestors().includes(submission.type) &&
      (submission.status === 'complete' || submission.status === 'pending'),
  )

  if (isInspectionsLoading || createInspectionSubmissionStatus === STATUS.LOADING || isFormLoading) {
    return <Loader />
  }

  if (inspectionsData.length === 0) {
    return (
      <div>
        <p style={{ textAlign: 'center' }}>No inspections found.</p>
        <p style={{ textAlign: 'center' }}>{inspectionsError?.message || ''}</p>
      </div>
    )
  }
  const downloadInspectionFile = (url, fileName) => {
    const link = document.createElement('a')
    link.href = url
    link.download = fileName || ''
    link.target = '_blank'
    link.rel = 'noopener noreferrer'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const isStreamlinedInspection = orderData && isStreamlinedInspectionOrCompletionReportOrder(orderData)
  const isAmpReview = orderData?.statusKey === ORDER_STATUS.AMP_REVIEW
  const shouldShowCompleteDataCollectionButton = isStreamlinedInspection && isAmpReview && is_amc_lender

  return (
    <>
      {inspectionSubmissionsError ? <Alert type="error" message="Error fetching submissions" /> : null}
      {createInspectionSubmissionError ? <Alert type="error" message="Error creating inspection submission" /> : null}
      {shouldShowCompleteDataCollectionButton && (
        <div style={{ marginBottom: '16px', marginLeft: '12px' }}>
          <CompleteDataCollectionButton refreshOrder={refreshOrder} />
        </div>
      )}
      <Row>
        <Col span={16}>
          <Card size="small" title="Submit Inspection to GSE" className="ml-3">
            <Form layout="inline">
              {/* Commented out the inspection dropdown for now */}
              {/* <Form.Item label="Inspections">
                <Select
                  value={inspectionId}
                  onChange={(value) => setInspectionId(value)}
                  options={inspectionsData.map((inspection, index) => ({
                    value: inspection._id,
                    label: `Inspection ${inspectionsData.length - index} - v${formVersion}`,
                  }))}
                />
              </Form.Item> */}
              <Form.Item label="Investor">
                <Select
                  value={investor}
                  onChange={(value) => setInvestor(value)}
                  options={INVESTOR_OPTIONS}
                  style={{ width: '200px' }}
                />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  disabled={!inspectionId || shouldDisableSubmit || !is_amc_lender}
                  onClick={onSubmit}
                  data-testid="submitInspectionButton"
                >
                  Submit
                </Button>
              </Form.Item>
            </Form>
          </Card>
          <div className="mt-2 ml-3">
            <InspectionBackofficeStatus orderId={orderId} />
          </div>
        </Col>
        <Col span={8}>
          <Card size="small" title="Wescan Floor Plan Files" className="ml-2 mr-3 h-100">
            <ul className="list-group" style={{ maxHeight: '150px', overflowY: 'auto' }}>
              {inspectionDetailWescanFiles.map((file) => (
                <li key={file._id} className="list-group-item p-1 m-0 border-0">
                  <div className="d-flex flex-wrap justify-content-between">
                    <span>{file?.metadata?.notes}</span>
                    <Button
                      type="primary"
                      size="small"
                      icon={<DownloadOutlined />}
                      onClick={() => downloadInspectionFile(file?.metadata?.serialized_values, file?.metadata?.notes)}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </Col>
      </Row>
      <InspectionSubmissionsView inspectionId={inspectionId} />
    </>
  )
}

export default InspectionSubmissionsTab
